import React from "react";
import { X, CaretLeft, CaretRight, ArrowSquareOut } from "@phosphor-icons/react";

/**
 * Iter-75.10 — Sample deck lightbox.
 *
 * Renders every sample landing page in a modal iframe carousel so the
 * operator can flip through the pitch deck without leaving admin.
 * Personalization inputs (firm + phone) update the iframe URL live —
 * every sample uses ?firm=…&phone=… honoured by CustomLandingPage.jsx
 * for `sample-` slugs (Iter-53.1d).
 *
 * Props:
 *   samples:  [{id, slug, firm_name, vertical, form_experience}]
 *   origin:   window.location.origin
 *   onClose:  () => void
 */
export default function SamplePreviewLightbox({ samples, origin, onClose }) {
  const [idx, setIdx] = React.useState(0);
  const [firm, setFirm] = React.useState("");
  const [phone, setPhone] = React.useState("");
  // Iter-75.11 — auto-rotate: 0 (off) | 5 | 10 | 15 seconds.
  const [autoSecs, setAutoSecs] = React.useState(0);
  // When the operator hovers the iframe, pause auto-advance so they
  // can read the page they're on.
  const [paused, setPaused] = React.useState(false);

  const clamp = React.useCallback((n) => {
    if (!samples.length) return 0;
    return ((n % samples.length) + samples.length) % samples.length;
  }, [samples.length]);

  const next = React.useCallback(() => setIdx((i) => clamp(i + 1)), [clamp]);
  const prev = React.useCallback(() => setIdx((i) => clamp(i - 1)), [clamp]);

  // Iter-75.11 — auto-rotate on a timer. Bail out cleanly on unmount,
  // pause on hover, and reset when the operator manually flips.
  React.useEffect(() => {
    if (!autoSecs || paused || samples.length < 2) return undefined;
    const t = setInterval(() => next(), autoSecs * 1000);
    return () => clearInterval(t);
  }, [autoSecs, paused, samples.length, next, idx]);

  // Keyboard navigation — Esc closes, arrows flip.
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, next, prev]);

  if (!samples.length) return null;
  const current = samples[idx] || samples[0];
  const qs = new URLSearchParams({ preview: "1" });
  if (firm.trim()) qs.set("firm", firm.trim());
  if (phone.trim()) qs.set("phone", phone.trim());
  const iframeUrl = `${origin}/lp/${current.slug}?${qs.toString()}`;

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/80 flex flex-col"
      data-testid="sample-preview-lightbox"
      role="dialog"
      aria-modal="true"
    >
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap bg-white border-b-2 border-black px-4 py-3">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="font-chivo font-black uppercase tracking-tight text-lg">
            Sample deck — preview
          </div>
          <div className="label-mono">
            {idx + 1} / {samples.length} · {current.firm_name || current.slug}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1 border border-black bg-emerald-50 px-2 py-1">
            <span className="text-[9px] uppercase tracking-widest text-emerald-900 font-bold">
              Personalize
            </span>
            <input
              type="text"
              value={firm}
              onChange={(e) => setFirm(e.target.value)}
              placeholder="Firm name"
              data-testid="lightbox-firm-input"
              className="w-32 border border-black px-2 py-1 text-[11px] font-mono bg-white"
            />
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Phone"
              data-testid="lightbox-phone-input"
              className="w-28 border border-black px-2 py-1 text-[11px] font-mono bg-white"
            />
          </div>
          <a
            href={iframeUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="lightbox-open-tab"
            className="border-2 border-black bg-white px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold hover:bg-black hover:text-white inline-flex items-center gap-1"
            title="Open this sample in a new tab"
          >
            <ArrowSquareOut size={11} weight="bold" /> New tab
          </a>
          <div className="border-2 border-black bg-white flex items-center gap-1 px-2 py-1"
               data-testid="lightbox-auto-controls">
            <span className="text-[9px] uppercase tracking-widest font-bold">Auto</span>
            {[0, 5, 10, 15].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setAutoSecs(n)}
                data-testid={`lightbox-auto-${n}`}
                className={
                  "text-[10px] uppercase tracking-widest px-1.5 py-0.5 font-bold border "
                  + (autoSecs === n
                    ? "bg-black text-white border-black"
                    : "bg-white text-zinc-700 border-zinc-300 hover:border-black")
                }
                title={n === 0 ? "Auto-advance off" : `Advance every ${n} seconds`}
              >
                {n === 0 ? "Off" : `${n}s`}
              </button>
            ))}
            {autoSecs > 0 && paused && (
              <span className="text-[9px] uppercase tracking-widest text-amber-700 font-bold ml-1"
                    data-testid="lightbox-auto-paused">
                Paused
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="lightbox-close"
            className="border-2 border-black bg-white p-2 hover:bg-black hover:text-white"
          >
            <X size={14} weight="bold" />
          </button>
        </div>
      </div>

      {/* Body — iframe + arrows */}
      <div className="flex-1 flex items-stretch min-h-0">
        <button
          type="button"
          onClick={prev}
          data-testid="lightbox-prev"
          className="w-14 flex items-center justify-center bg-white/10 hover:bg-white/25 text-white"
          title="Previous (←)"
        >
          <CaretLeft size={28} weight="bold" />
        </button>
        <div
          className="flex-1 bg-black flex items-center justify-center p-2"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          data-testid="lightbox-iframe-wrap"
        >
          <div className="w-full h-full bg-white border-2 border-black overflow-hidden shadow-[8px_8px_0_rgba(255,255,255,0.15)]">
            <iframe
              key={iframeUrl}
              src={iframeUrl}
              title={`Sample preview: ${current.slug}`}
              className="w-full h-full"
              data-testid="lightbox-iframe"
              sandbox="allow-forms allow-scripts allow-same-origin allow-popups"
            />
          </div>
        </div>
        <button
          type="button"
          onClick={next}
          data-testid="lightbox-next"
          className="w-14 flex items-center justify-center bg-white/10 hover:bg-white/25 text-white"
          title="Next (→)"
        >
          <CaretRight size={28} weight="bold" />
        </button>
      </div>

      {/* Footer — thumbnail strip so operator sees what's next. */}
      <div className="bg-white border-t-2 border-black px-3 py-2 overflow-x-auto">
        <div className="flex items-center gap-2 min-w-max">
          {samples.map((s, i) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setIdx(i)}
              data-testid={`lightbox-thumb-${s.slug}`}
              className={
                "flex-shrink-0 border-2 px-2 py-1 text-[10px] uppercase tracking-widest font-bold "
                + (i === idx
                  ? "bg-black text-white border-black"
                  : "bg-white text-zinc-800 border-zinc-300 hover:border-black")
              }
              title={s.slug}
            >
              {i + 1}. {(s.firm_name || s.slug).slice(0, 22)}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
